import { useState } from 'react';
import { useReflections } from '../contexts/ReflectionsContext';
import ReflectionSheet from '../components/ReflectionSheet';
import { moods } from '../constants/moods';

const reflectionPrompts = [
  "What's one thing you're grateful for today?",
  "What's a challenge you overcame recently?",
  "What's something you learned today?",
  "What's one thing you'd like to improve?",
  "What made you smile today?",
  "What's a goal you're working towards?",
  "What's something kind you did for someone?",
  "What's a recent accomplishment you're proud of?"
];

export default function ReflectionZone() {
  const { reflections, loading: reflectionsLoading, addReflection } = useReflections();
  const [isSaving, setIsSaving] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(() => {
    const randomIndex = Math.floor(Math.random() * reflectionPrompts.length);
    return reflectionPrompts[randomIndex];
  });
  const [entry, setEntry] = useState('');
  const [mood, setMood] = useState(null);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [showReflectionSheet, setShowReflectionSheet] = useState(false);

  const handleNewPrompt = () => {
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * reflectionPrompts.length);
    } while (reflectionPrompts[newIndex] === currentPrompt);
    setCurrentPrompt(reflectionPrompts[newIndex]);
  };

  const handleSave = async () => {
    if (!entry.trim() || !mood) return;

    setIsSaving(true);
    try {
      await addReflection({
        entry,
        mood,
        prompt: currentPrompt
      });
      
      // Reset form
      setEntry('');
      setMood(null);
      // Get new prompt
      handleNewPrompt();
    } catch (error) {
      console.error('Error saving reflection:', error);
      // TODO: Show error toast
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-1 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Reflection Zone</h1>
          <p className="mt-2 text-gray-600">Take a moment to pause and reflect</p>
        </div>

        {/* Mood Selection */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">How are you feeling?</h2>
          <div className="grid grid-cols-3 gap-3">
            {moods.map((m) => (
              <button
                key={m.name}
                onClick={() => setMood(m)}
                className={`flex flex-col items-center p-4 rounded-xl transition-all ${
                  mood?.name === m.name
                    ? 'bg-primary-50 ring-2 ring-primary-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                <span className="text-2xl">{m.emoji}</span>
                <span className="text-sm font-medium text-gray-600 mt-2">{m.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Writing Prompt */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-lg font-medium text-gray-900">Today's Prompt</h2>
            <button
              onClick={handleNewPrompt}
              className="text-primary-600 hover:text-primary-700"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600 mb-4 italic">{currentPrompt}</p>
          <textarea
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            placeholder="Start writing here..."
            rows={6}
            className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={!entry.trim() || !mood || isSaving}
          className="w-full py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Entry'}
        </button>

        {/* Previous Entries Preview */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Reflections</h2>
          <div className="space-y-4">
            {reflectionsLoading ? (
              // Loading skeleton
              [...Array(3)].map((_, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-xl animate-pulse">
                  <div className="flex justify-between items-start mb-2">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="w-20 h-4 bg-gray-200 rounded"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
                    <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : reflections.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No reflections yet. Start your journaling journey today!
              </div>
            ) : (
              reflections.map((reflection) => (
                <div 
                  key={reflection.id} 
                  className="p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setSelectedReflection(reflection);
                    setShowReflectionSheet(true);
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-2xl">{reflection.mood.emoji}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(reflection.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-600 line-clamp-3">{reflection.entry}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ReflectionSheet 
        isOpen={showReflectionSheet}
        onClose={() => {
          setShowReflectionSheet(false);
          setSelectedReflection(null);
        }}
        reflection={selectedReflection}
      />
    </div>
  );
} 